import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, DatePicker, Form, Input, Modal, Popconfirm, Space, Table, Tag, Upload, message } from 'antd';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../api/http';
import { buildMinioProxyUrl, fetchBlobUrl } from '../../utils/authedResource';
import { useAuth } from '../../context/AuthContext';
import AuthedImage from '../../components/AuthedImage';

const MyProfilePage = () => {
  const auth = useAuth();
  const canManage = auth.hasPermission(['my_profile_manage']);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    id_card_front_object_name: null,
    id_card_front_url: null,
    id_card_back_object_name: null,
    id_card_back_url: null,
    certificates: []
  });
  const [notices, setNotices] = useState([]);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [currentNotice, setCurrentNotice] = useState(null);
  const [signOpen, setSignOpen] = useState(false);
  const [signNotice, setSignNotice] = useState(null);
  const [signDate, setSignDate] = useState(dayjs());
  const [signatureDataUrl, setSignatureDataUrl] = useState('');
  const [drawing, setDrawing] = useState(false);
  const [frontPreviewUrl, setFrontPreviewUrl] = useState('');
  const [backPreviewUrl, setBackPreviewUrl] = useState('');

  const [certOpen, setCertOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [certForm] = Form.useForm();
  const [certUploading, setCertUploading] = useState(false);
  const certImageUrlWatch = Form.useWatch('image_url', certForm);
  const certImageObjectWatch = Form.useWatch('image_object_name', certForm);

  const [canvasRef, setCanvasRef] = useState(null);

  const resolveObjectPreview = async (objectName, filename) => {
    if (!objectName) return '';
    const previewResp = await api.get(`/files/preview?objectName=${encodeURIComponent(objectName)}`);
    const resolvedObjectName = previewResp?.data?.objectName || objectName;
    const proxyUrl = buildMinioProxyUrl(resolvedObjectName, { filename: filename || resolvedObjectName.split('/').pop() || 'preview' });
    if (!proxyUrl) return '';
    return await fetchBlobUrl(proxyUrl);
  };

  const refreshIdCardPreview = async (nextProfile) => {
    try {
      if (frontPreviewUrl) URL.revokeObjectURL(frontPreviewUrl);
      if (backPreviewUrl) URL.revokeObjectURL(backPreviewUrl);
    } catch (e) { /* ignore */ }
    let front = '';
    let back = '';
    try {
      if (nextProfile?.id_card_front_object_name) {
        front = await resolveObjectPreview(nextProfile.id_card_front_object_name, '身份证正面');
      }
      if (nextProfile?.id_card_back_object_name) {
        back = await resolveObjectPreview(nextProfile.id_card_back_object_name, '身份证反面');
      }
    } catch (e) {
      message.warning('身份证预览生成失败，可重新上传或稍后重试');
    }
    setFrontPreviewUrl(front);
    setBackPreviewUrl(back);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/onboarding-materials/my');
      setProfile({
        id_card_front_object_name: data?.profile?.id_card_front_object_name || null,
        id_card_front_url: data?.profile?.id_card_front_url || null,
        id_card_back_object_name: data?.profile?.id_card_back_object_name || null,
        id_card_back_url: data?.profile?.id_card_back_url || null,
        certificates: Array.isArray(data?.profile?.certificates) ? data.profile.certificates : []
      });
      await refreshIdCardPreview({
        id_card_front_object_name: data?.profile?.id_card_front_object_name || null,
        id_card_back_object_name: data?.profile?.id_card_back_object_name || null
      });
      setNotices(Array.isArray(data?.notices) ? data.notices : []);
    } catch (e) {
      message.error(e.response?.data?.error || e.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    return () => {
      try {
        if (frontPreviewUrl) URL.revokeObjectURL(frontPreviewUrl);
        if (backPreviewUrl) URL.revokeObjectURL(backPreviewUrl);
      } catch (e) { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveProfile = async (nextProfile) => {
    setSaving(true);
    try {
      await api.put('/onboarding-materials/my', nextProfile);
      setProfile(nextProfile);
      await refreshIdCardPreview(nextProfile);
      message.success('保存成功');
    } catch (e) {
      message.error(e.response?.data?.error || e.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const uploadOne = async (file, side) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('path', 'my-profile/id-cards');
    fd.append('category', 'id_cards');

    try {
      const res = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const first = res.data?.files?.[0];
      if (!first) throw new Error('上传结果为空');
      const next = {
        ...profile,
        [side === 'front' ? 'id_card_front_object_name' : 'id_card_back_object_name']: first.objectName,
        [side === 'front' ? 'id_card_front_url' : 'id_card_back_url']: first.url
      };
      await saveProfile(next);
    } catch (e) {
      message.error(e.response?.data?.error || e.message || '上传失败');
    }
    return false;
  };

  const clearIdCard = async (side) => {
    const next = {
      ...profile,
      [side === 'front' ? 'id_card_front_object_name' : 'id_card_back_object_name']: null,
      [side === 'front' ? 'id_card_front_url' : 'id_card_back_url']: null
    };
    await saveProfile(next);
  };

  const openAddCert = () => {
    setEditingIndex(-1);
    certForm.resetFields();
    certForm.setFieldsValue({ image_object_name: '', image_url: '' });
    setCertOpen(true);
  };

  const openEditCert = (record, index) => {
    setEditingIndex(index);
    certForm.setFieldsValue({ ...(record || {}), image_object_name: record?.image_object_name || '', image_url: record?.image_url || '' });
    setCertOpen(true);
  };

  const uploadCertImage = async (file) => {
    if (!canManage) return false;
    setCertUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('path', 'my-profile/certificates');
      fd.append('category', 'certificates');
      const res = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const first = res.data?.files?.[0];
      if (!first) throw new Error('上传结果为空');
      certForm.setFieldsValue({ image_object_name: first.objectName, image_url: first.url });
      message.success('证件图片上传成功');
    } catch (e) {
      message.error(e.response?.data?.error || e.message || '证件图片上传失败');
    } finally {
      setCertUploading(false);
    }
    return false;
  };

  const resolveCertImageSrc = (record) => {
    if (record?.preview_src) return record.preview_src;
    const objectOrUrl = record?.image_object_name || record?.image_url || '';
    if (!objectOrUrl) return '';
    return buildMinioProxyUrl(objectOrUrl, { filename: '证件图片' }) || record?.image_url || '';
  };

  const mergedCertificateRows = useMemo(() => {
    const idCardRows = [
      {
        __builtin: true,
        __side: 'front',
        __rowKey: 'id_card_front',
        name: '身份证正面',
        number: '',
        expiry_date: '',
        issuer: '',
        remark: '对应字段：id_card_front_object_name / id_card_front_url',
        image_object_name: profile.id_card_front_object_name || '',
        image_url: profile.id_card_front_url || '',
        preview_src: frontPreviewUrl || profile.id_card_front_url || ''
      },
      {
        __builtin: true,
        __side: 'back',
        __rowKey: 'id_card_back',
        name: '身份证反面',
        number: '',
        expiry_date: '',
        issuer: '',
        remark: '对应字段：id_card_back_object_name / id_card_back_url',
        image_object_name: profile.id_card_back_object_name || '',
        image_url: profile.id_card_back_url || '',
        preview_src: backPreviewUrl || profile.id_card_back_url || ''
      }
    ];
    return idCardRows.concat(profile.certificates || []);
  }, [profile, frontPreviewUrl, backPreviewUrl]);

  const submitCert = async () => {
    try {
      const values = await certForm.validateFields();
      const nextCertificates = [...(profile.certificates || [])];
      if (editingIndex >= 0) nextCertificates[editingIndex] = values;
      else nextCertificates.push(values);
      await saveProfile({ ...profile, certificates: nextCertificates });
      setCertOpen(false);
    } catch (e) {
      if (!e?.errorFields) message.error(e.response?.data?.error || e.message || '保存证件失败');
    }
  };

  const deleteCert = async (index) => {
    const nextCertificates = [...(profile.certificates || [])];
    nextCertificates.splice(index, 1);
    await saveProfile({ ...profile, certificates: nextCertificates });
  };

  const signNoticeAction = async () => {
    if (!signNotice?.id) return;
    const dataUrl = signatureDataUrl || (canvasRef ? canvasRef.toDataURL('image/png') : '');
    if (!/^data:image\/.+;base64,/.test(String(dataUrl || ''))) {
      message.warning('请先手写签名');
      return;
    }
    try {
      await api.post('/onboarding-materials/my/sign', {
        template_id: signNotice.id,
        signed_date: signDate ? signDate.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        signature_data: dataUrl
      });
      message.success('签署成功');
      setSignOpen(false);
      setSignNotice(null);
      setSignatureDataUrl('');
      await loadData();
    } catch (e) {
      message.error(e.response?.data?.error || e.message || '签署失败');
    }
  };

  const openSignModal = (notice) => {
    setSignNotice(notice);
    setSignDate(dayjs());
    setSignatureDataUrl('');
    setSignOpen(true);
    setTimeout(() => {
      if (!canvasRef) return;
      const ctx = canvasRef.getContext('2d');
      if (!ctx) return;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvasRef.width, canvasRef.height);
    }, 0);
  };

  const getCanvasPoint = (evt) => {
    if (!canvasRef) return null;
    const rect = canvasRef.getBoundingClientRect();
    const touch = evt.touches && evt.touches[0] ? evt.touches[0] : null;
    const clientX = touch ? touch.clientX : evt.clientX;
    const clientY = touch ? touch.clientY : evt.clientY;
    return {
      x: ((clientX - rect.left) * canvasRef.width) / rect.width,
      y: ((clientY - rect.top) * canvasRef.height) / rect.height
    };
  };

  const startDraw = (evt) => {
    if (!canvasRef) return;
    const point = getCanvasPoint(evt);
    if (!point) return;
    const ctx = canvasRef.getContext('2d');
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#222';
    setDrawing(true);
  };

  const draw = (evt) => {
    if (!drawing || !canvasRef) return;
    const point = getCanvasPoint(evt);
    if (!point) return;
    const ctx = canvasRef.getContext('2d');
    if (!ctx) return;
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    setSignatureDataUrl(canvasRef.toDataURL('image/png'));
  };

  const endDraw = () => {
    setDrawing(false);
  };

  const clearSign = () => {
    if (!canvasRef) return;
    const ctx = canvasRef.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvasRef.width, canvasRef.height);
    setSignatureDataUrl('');
  };

  const certColumns = [
    { title: '证件名称', dataIndex: 'name', width: 160 },
    { title: '证件编号', dataIndex: 'number', width: 180, render: (v) => v || '-' },
    { title: '到期日', dataIndex: 'expiry_date', width: 140, render: (v) => v || '-' },
    { title: '发证机关', dataIndex: 'issuer', width: 180, render: (v) => v || '-' },
    {
      title: '证件图片',
      dataIndex: 'image_url',
      width: 130,
      render: (_, record) => {
        const src = resolveCertImageSrc(record);
        if (!src) return '-';
        return <AuthedImage src={src} alt="证件图片" style={{ width: 56, height: 38, objectFit: 'cover', border: '1px solid #eee' }} />;
      }
    },
    { title: '备注', dataIndex: 'remark', render: (v) => v || '-' },
    {
      title: '操作',
      width: 220,
      render: (_, record, index) => (
        record.__builtin ? (
          <Space>
            <Upload showUploadList={false} beforeUpload={(file) => uploadOne(file, record.__side)}>
              <Button size="small" icon={<UploadOutlined />} loading={saving} disabled={!canManage}>上传</Button>
            </Upload>
            <Popconfirm title="确认清空该身份证图片？" onConfirm={() => clearIdCard(record.__side)}>
              <Button size="small" danger disabled={!canManage || !record.image_object_name}>清空</Button>
            </Popconfirm>
          </Space>
        ) : (
          <Space>
            <Button size="small" onClick={() => openEditCert(record, index - 2)}>编辑</Button>
            <Popconfirm title="确认删除该证件？" onConfirm={() => deleteCert(index - 2)}>
              <Button size="small" danger>删除</Button>
            </Popconfirm>
          </Space>
        )
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 12 }}>我的资料</h2>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card title="证件管理" extra={<Button type="primary" icon={<PlusOutlined />} onClick={openAddCert} disabled={!canManage}>新增证件</Button>} loading={loading}>
          <Table
            size="small"
            rowKey={(r, i) => r.__rowKey || `${r.name || 'cert'}_${i}`}
            columns={certColumns}
            dataSource={mergedCertificateRows}
            pagination={false}
            scroll={{ x: 900 }}
          />
        </Card>

        <Card title="签订告知书" loading={loading}>
          <Table
            size="small"
            rowKey="id"
            dataSource={notices}
            pagination={false}
            columns={[
              { title: '标题', dataIndex: 'title' },
              { title: '状态', dataIndex: 'status', width: 100, render: (v) => <Tag color={v === 'active' ? 'green' : 'default'}>{v === 'active' ? '启用' : '停用'}</Tag> },
              { title: '签署状态', width: 120, render: (_, r) => r.signed ? <Tag color="green">已签署</Tag> : <Tag color="orange">未签署</Tag> },
              { title: '签署时间', dataIndex: 'signed_at', width: 180, render: (v) => v || '-' },
              {
                title: '操作',
                width: 220,
                render: (_, r) => (
                  <Space>
                    <Button size="small" onClick={() => { setCurrentNotice(r); setNoticeOpen(true); }}>查看内容</Button>
                    <Button size="small" type="primary" disabled={!canManage || r.signed || r.status !== 'active'} onClick={() => openSignModal(r)}>签署</Button>
                  </Space>
                )
              }
            ]}
          />
        </Card>
      </Space>

      <Modal open={certOpen} title={editingIndex >= 0 ? '编辑证件' : '新增证件'} onOk={submitCert} onCancel={() => setCertOpen(false)} destroyOnClose okButtonProps={{ disabled: !canManage }}>
        <Form form={certForm} layout="vertical">
          <Form.Item name="name" label="证件名称" rules={[{ required: true, message: '请输入证件名称' }]}>
            <Input placeholder="例如：电工证" />
          </Form.Item>
          <Form.Item name="number" label="证件编号">
            <Input placeholder="请输入证件编号" />
          </Form.Item>
          <Form.Item name="expiry_date" label="到期日期">
            <Input placeholder="例如：2028-12-31" />
          </Form.Item>
          <Form.Item name="issuer" label="发证机关">
            <Input placeholder="请输入发证机关" />
          </Form.Item>
          <Form.Item name="image_url" label="证件图片URL" style={{ display: 'none' }}>
            <Input />
          </Form.Item>
          <Form.Item name="image_object_name" label="证件图片对象名" style={{ display: 'none' }}>
            <Input />
          </Form.Item>
          <Form.Item label="证件图片上传">
            <Space direction="vertical">
              {(certImageObjectWatch || certImageUrlWatch) ? (
                <AuthedImage
                  src={buildMinioProxyUrl(certImageObjectWatch || certImageUrlWatch, { filename: '证件图片' }) || certImageUrlWatch}
                  alt="证件图片"
                  style={{ width: 240, maxHeight: 120, objectFit: 'cover', border: '1px solid #eee' }}
                />
              ) : (
                <div style={{ width: 240, height: 120, border: '1px dashed #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>未上传证件图片</div>
              )}
              <Upload showUploadList={false} beforeUpload={uploadCertImage}>
                <Button icon={<UploadOutlined />} disabled={!canManage} loading={certUploading}>上传证件图片</Button>
              </Upload>
            </Space>
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={noticeOpen}
        title={currentNotice ? currentNotice.title : '告知书内容'}
        footer={<Button onClick={() => setNoticeOpen(false)}>关闭</Button>}
        onCancel={() => setNoticeOpen(false)}
        width={760}
      >
        <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>{currentNotice?.display_content || currentNotice?.content || ''}</div>
        {currentNotice?.signed_date && (
          <div style={{ marginTop: 16, color: '#555' }}>签订日期：{currentNotice.signed_date}</div>
        )}
        {currentNotice?.signature_data && (
          <div style={{ marginTop: 8 }}>
            <div style={{ marginBottom: 6, color: '#555' }}>手写签名：</div>
            <img src={currentNotice.signature_data} alt="手写签名" style={{ border: '1px solid #eee', width: 320, maxWidth: '100%' }} />
          </div>
        )}
      </Modal>

      <Modal
        open={signOpen}
        title={signNotice ? `签署告知书 - ${signNotice.title}` : '签署告知书'}
        onCancel={() => { setSignOpen(false); setSignNotice(null); }}
        onOk={signNoticeAction}
        okText="确认签署"
        cancelText="取消"
        width={780}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto', padding: 12, background: '#fafafa', border: '1px solid #f0f0f0' }}>
            {signNotice?.content || ''}
          </div>
          <Space align="center">
            <span>签订日期：</span>
            <DatePicker value={signDate} onChange={(d) => setSignDate(d || dayjs())} />
          </Space>
          <div>
            <div style={{ marginBottom: 6 }}>请在下方手写签名：</div>
            <canvas
              ref={(el) => setCanvasRef(el)}
              width={700}
              height={180}
              style={{ width: '100%', border: '1px dashed #c9c9c9', background: '#fff', touchAction: 'none' }}
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={endDraw}
              onMouseLeave={endDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={endDraw}
            />
            <div style={{ marginTop: 8 }}>
              <Button size="small" onClick={clearSign}>清空签名</Button>
            </div>
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default MyProfilePage;
